/**
 * 🏋️ Peacock Gym — Roc's bodybuilding temple. Hub for the lift/training side
 * of OmniMuscle: Arena · AI Coach · Food Log · Daily Vitals · Cardio Tracker.
 *
 * Future Phase 2 (locked spec, deferred): interactive gym lifts/PRs/leaderboards.
 * This page ships as a hub + NPC dialog with Roc + journal-aware response.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import ReceiptScanner from "@/components/ReceiptScanner";
import HeroBiometricsCard from "@/components/HeroBiometricsCard";
import HeroPortraitCard  from "@/components/HeroPortraitCard";
import RpmAvatarCard     from "@/components/RpmAvatarCard";
import GpsCheckInCard   from "@/components/GpsCheckInCard";
import WeighInReminderBanner from "@/components/WeighInReminderBanner";
import DailyLogPanel from "@/components/DailyLogPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HUB_LINKS = [
  { to: "/arena",       icon: "⚔️",  title: "Arena",          desc: "Fight bosses, earn Battle Coins, win loot." },
  { to: "/coliseum",    icon: "🏟️",  title: "Coliseum",       desc: "3v3 PvP · Party Dungeons · party up by handle." },
  { to: "/#ai-coach",   icon: "🤖",  title: "AI Coach",       desc: "Personalized programming + form cues." },
  { to: "/cardio",      icon: "🏃",  title: "Cardio Tracker", desc: "Log runs, sprints, intervals." },
  { to: "/tracker-calendar", icon: "📅", title: "Tracker Calendar", desc: "Full month view — log missed food, strength & cardio on any day." },
  { to: "/#food",       icon: "🥗",  title: "Food Log",       desc: "Macros, micros, snap-a-meal photo." },
  { to: "/#vitals",     icon: "💉",  title: "Daily Vitals",   desc: "Sleep, water, weight, BP — logs straight into your Food Log & streaks." },
  { to: "/town-hall",   icon: "🪪",  title: "Licenses",       desc: "Weasley issues licenses — Roc's gym uses them at the smith." },
  { to: "/budget",      icon: "💰",  title: "Budget",         desc: "Track income vs expenses. Discipline outside the gym = gains inside it." },
];

// 💀 Roc's Stack — Blackstone Labs products he swears by. Lore-flavored
// shout-outs, no affiliate links (yet). When affiliate URLs land, drop them
// into `url` and the card becomes click-through.
const ROC_STACK = [
  { id: "dust_x",    icon: "💀⚡", name: "Dust X",      tier: "Pre-Workout",
    line: "\"My morning sermon. One scoop and I see the barbell BEFORE I touch it.\"" },
  { id: "apex_male", icon: "👑",   name: "Apex Male",   tier: "Test Booster",
    line: "\"Natural test support. The kingdom doesn't pay for itself.\"" },
  { id: "eradicate", icon: "🚫💧", name: "Eradicate",   tier: "Estrogen Control",
    line: "\"Keep the chest dry, the jaw sharp. Tight stuff, friend.\"" },
  { id: "brain_force", icon: "🧠⚡", name: "Brain Force", tier: "Nootropic",
    line: "\"For the cardio days. So I don't fall asleep mid-stride.\"" },
  { id: "glycolog",  icon: "🍚",   name: "Glycolog",    tier: "Glucose Disposal",
    line: "\"Cheat meal companion. The carbs go where they're needed.\"" },
];


export default function GymPage() {
  const { isAuthed } = useAuth();
  const [state, setState] = useState(null);
  const [rocResponse, setRocResponse] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/onboarding/state`);
      setState(r.data);
    } catch {}
  };

  useEffect(() => {
    if (!isAuthed) return;
    refresh();
  }, [isAuthed]);

  const showRoc = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/onboarding/ask-npc`, { npc_id: "roc" });
      setRocResponse(r.data.npc);
      setState(r.data.state);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Roc is mid-set.");
    } finally {
      setBusy(false);
    }
  };

  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#10001a] via-black to-[#1a0010] text-amber-100">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <div className="text-6xl mb-4">🦚</div>
          <h1 className="text-4xl font-black mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
            THE PEACOCK GYM
          </h1>
          <p className="text-neutral-400">Sign in to lift with Roc.</p>
        </div>
        <Footer />
      </div>
    );
  }

  const stage = state?.stage || "none";
  const hasJournal = !!state?.journal;
  const isAsking = ["asking_around", "dave_overwhelmed", "gathering"].includes(stage);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#001020] via-black to-[#1a0010] text-amber-100" data-testid="gym-page">
      <Header />

      <main className="max-w-6xl mx-auto px-4 pt-20 md:pt-24 py-6 pb-24">
        {/* Hero */}
        <div className="rounded-2xl border border-amber-700/40 bg-gradient-to-br from-[#0a1a2a] via-black/60 to-[#1a0010] p-6 mb-6 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 text-[160px] opacity-10 select-none">🦚</div>
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.4em] text-amber-300/70 mb-1">Kingdom of Dragonious</div>
            <h1 className="text-4xl md:text-5xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
              🦚 THE PEACOCK GYM
            </h1>
            <p className="text-amber-100/80 text-sm mt-2 max-w-2xl">
              Owner: <b>Roc</b> · or "OmniMuscle, just Roc" if he likes you · a bodybuilding Phoenix-Roc
              hybrid the size of a small barn. Where the kingdom's lifters lift.
            </p>
            <div className="mt-4">
              <button
                onClick={() => setScannerOpen(true)}
                className="px-4 py-2 rounded-full bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-sm text-amber-200 font-bold tracking-wide transition"
                title="Scan a grocery receipt into Cassandra's Ledger"
                data-testid="gym-open-receipt-scanner"
              >
                📷 Scan Receipt · Cassandra's Ledger
              </button>
            </div>
          </div>
        </div>

        <WeighInReminderBanner />

        {/* 📊 Unified Daily Log — same log seen in Kitchen, Tracker, Calendar, AI Coach */}
        <div className="mb-6" data-testid="gym-daily-log">
          <DailyLogPanel title="Today's Training + Meals" />
        </div>

        <HeroPortraitCard />
        <RpmAvatarCard />

        <HeroBiometricsCard />

        <GpsCheckInCard />
        <div className="rounded-2xl border-2 border-purple-700/50 bg-gradient-to-br from-[#10001a] to-black/60 p-6 mb-6">
          <div className="flex items-start gap-5">
            <div className="text-6xl flex-shrink-0">💪🦅</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300/70">The Owner</div>
              <h2 className="text-2xl font-black text-amber-200 mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
                ROC
              </h2>
              <p className="text-sm text-amber-100/85 italic font-serif leading-relaxed">
                "Most days I'm under a barbell. The other days I'm <i>thinking</i> about a barbell.
                You want to lift? Pick a corner. You want to talk? Pick another gym."
              </p>
              <div className="text-[11px] text-neutral-500 italic mt-3">
                Massive · Disinterested · Doesn't read · Genuinely loves a good pump
              </div>

              {isAsking && hasJournal && !rocResponse && (
                <button
                  onClick={showRoc}
                  disabled={busy}
                  data-testid="show-roc-journal-btn"
                  className="mt-4 px-5 py-2 rounded bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold text-sm"
                >
                  📜 Show Roc the Journal
                </button>
              )}

              {rocResponse && (
                <div className="mt-4 rounded-xl border-2 border-amber-700/50 bg-amber-900/10 p-5">
                  <div className="text-[11px] uppercase tracking-widest text-purple-300/70 mb-2">{rocResponse.title}</div>
                  <div className="space-y-2 text-base text-amber-100/95 leading-relaxed font-serif">
                    {rocResponse.lines.map((line, i) => (
                      <p key={i} data-testid={`roc-line-${i}`}>{line}</p>
                    ))}
                  </div>
                  <div className="mt-4 text-[11px] text-amber-300 italic">
                    → Roc says to find <b>Dave</b> at the diner.
                  </div>
                  <Link
                    to="/diner"
                    className="inline-block mt-3 px-4 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs"
                  >
                    🐦‍🔥 To Phoenix Flame Diner →
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 🦸 Hero Class entry point — Roc owns this unlock */}
        <RocHeroClassCard />

        {/* 💀 Roc's Stack — Blackstone Labs */}
        <div className="rounded-2xl border border-amber-700/40 bg-gradient-to-br from-[#1a0a00] via-black/60 to-[#0a0500] p-5 mb-6" data-testid="roc-stack">
          <div className="flex items-center gap-3 mb-1">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/70">💀 The Owner's Stack</div>
            <div className="h-px flex-1 bg-gradient-to-r from-amber-700/40 to-transparent" />
            <div className="text-[10px] text-purple-300/60 italic">presented by <b>Blackstone Labs</b></div>
          </div>
          <h2 className="text-2xl font-black text-amber-200 mb-3" style={{ fontFamily: "Anton, sans-serif" }}>
            ROC'S DAILY KIT
          </h2>
          <p className="text-sm text-amber-100/80 italic font-serif leading-relaxed mb-4">
            "If it ain't on this shelf, it ain't in my bloodstream. Train hard.
            Eat sharp. Recover like you mean it." — <b>Roc</b>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {ROC_STACK.map((p) => (
              <div
                key={p.id}
                data-testid={`stack-${p.id}`}
                className="rounded-lg border border-purple-700/40 bg-black/40 p-3 hover:border-amber-500 transition"
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-2xl">{p.icon}</div>
                  <div>
                    <div className="text-sm font-black text-amber-200 leading-tight">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-purple-300/70">{p.tier}</div>
                  </div>
                </div>
                <div className="text-[11px] text-amber-100/85 italic font-serif leading-snug">{p.line}</div>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-neutral-500 italic mt-3">
            ⚠️ In-game flavor only. Always consult a doctor before starting any supplement protocol.
          </div>
        </div>

        {/* 🛡️ Roc's Battlegrounds Invitation — narrative thread (Feb 2026) */}
        <Link
          to="/battlegrounds"
          data-testid="gym-battlegrounds-invite"
          className="block rounded-2xl border-2 border-amber-700/50 bg-gradient-to-br from-[#1a0500] via-black/70 to-[#0a0a1a] p-5 mb-6 hover:border-amber-500/80 hover:shadow-[0_0_30px_-12px_rgba(245,158,11,0.6)] transition-all group"
        >
          <div className="flex items-start gap-4">
            <div className="text-5xl flex-shrink-0 group-hover:scale-110 transition-transform">🛡️</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/70 mb-1">
                Roc cracks his neck · looks over at you
              </div>
              <div className="text-xl md:text-2xl font-black text-amber-100 mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
                "READY FOR THE BATTLEGROUNDS?"
              </div>
              <p className="text-sm text-amber-100/85 italic font-serif leading-relaxed">
                "I've been training out there for a while. 10v10. Cargo runs, Sentinels, Honor on the line.
                You wanna run with me? Queue's open." — <b>Roc</b>
              </p>
              <div className="text-[11px] text-amber-300/70 mt-2 flex items-center gap-2">
                <span className="px-1.5 py-0.5 rounded bg-amber-900/40 border border-amber-700/50">10v10</span>
                <span className="px-1.5 py-0.5 rounded bg-amber-900/40 border border-amber-700/50">Honor</span>
                <span className="px-1.5 py-0.5 rounded bg-amber-900/40 border border-amber-700/50">Sentinels</span>
                <span className="ml-auto text-amber-300 group-hover:translate-x-1 transition-transform">Enter →</span>
              </div>
            </div>
          </div>
        </Link>

        {/* Phase 2 teaser */}
        <div className="rounded-2xl border border-purple-700/40 bg-gradient-to-br from-[#10001a] to-black/40 p-5 mb-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300/70 mb-1">🚧 Coming Phase 2</div>
          <div className="text-lg font-black text-amber-200 mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
            INTERACTIVE GYM
          </div>
          <p className="text-sm text-amber-100/80 leading-relaxed">
            Real lift logging · PR tracker · weekly leaderboards · class-based stat boosts for Hero
            Classes (Warrior PATK, Mage MATK, Archer crit, Rogue dodge). Roc will start grading
            your sets. For now — he just lifts.
          </p>
        </div>

        {/* Hub — links to the existing training/food features */}
        <div className="mb-3 flex items-center gap-3">
          <h2 className="text-2xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
            🏋️ TRAINING HUB
          </h2>
          <div className="h-px flex-1 bg-gradient-to-r from-purple-700/60 to-transparent" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="gym-hub-grid">
          {HUB_LINKS.map((h) => (
            <Link
              key={h.title}
              to={h.to}
              data-testid={`gym-hub-${h.title.toLowerCase().replace(/\s/g, "-")}`}
              className="rounded-xl border border-purple-700/40 bg-gradient-to-br from-[#10001a] to-black/40 hover:border-amber-500 hover:shadow-[0_0_20px_rgba(245,158,11,0.18)] transition p-4 block"
            >
              <div className="text-4xl mb-2">{h.icon}</div>
              <div className="text-base font-black text-amber-200">{h.title}</div>
              <div className="text-[11px] text-neutral-400 mt-1">{h.desc}</div>
              <div className="text-[10px] text-amber-300/70 mt-2">Open →</div>
            </Link>
          ))}
        </div>
      </main>

      <ReceiptScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        capturedFrom="gym"
      />
      <Footer />
    </div>
  );
}



/**
 * 🦸 RocHeroClassCard — the Gym is the entry point for Hero Classes.
 * Roc-themed flavor + status pill. Phase 2 will gate behind a Roc quest;
 * V1 lets the user click through directly.
 */
function RocHeroClassCard() {
  const [hc, setHc] = useState(null);
  useEffect(() => {
    axios.get(`${API}/hero-class/state`).then((r) => setHc(r.data)).catch(() => {});
  }, []);
  const selected = hc?.selected;
  return (
    <div
      data-testid="roc-hero-class-card"
      className="rounded-2xl border-2 border-amber-500 bg-gradient-to-br from-amber-900/30 via-[#1a0a00] to-black/60 p-6 mb-6 shadow-[0_0_30px_rgba(245,158,11,0.2)]"
    >
      <div className="flex items-start gap-5 flex-wrap">
        <div className="text-6xl flex-shrink-0">💪🦅</div>
        <div className="flex-1 min-w-[260px]">
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-300/80 mb-1">Roc's Roster</div>
          <h2 className="text-3xl md:text-4xl font-black text-amber-200" style={{ fontFamily: "Anton, sans-serif" }}>
            CHOOSE YOUR HERO CLASS
          </h2>
          <p className="text-sm text-amber-100/90 italic font-serif mt-2 leading-relaxed">
            "Pick a path, Phoenix. Warrior. Mage. Archer. Ash Rogue. Four iron
            stances, four kinds of damage, one kingdom. The smith pours you a
            different weapon depending on what you choose. Choose wrong... well,
            you only choose once, kid."
          </p>
          {selected ? (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="px-3 py-1.5 rounded-full bg-amber-500 text-black text-xs font-black">
                {hc.class_meta?.icon} {hc.class_meta?.name?.toUpperCase()} · Lv {hc.level}
              </span>
              <Link
                to="/hero-class"
                className="px-4 py-1.5 rounded bg-purple-700 hover:bg-purple-600 text-white font-bold text-xs"
              >
                📊 View Stats
              </Link>
              {hc.rebirth_eligibility?.eligible && (
                <Link
                  to="/hero-class"
                  className="px-4 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white font-black text-xs animate-pulse"
                >
                  🔥 Rebirth Available!
                </Link>
              )}
            </div>
          ) : (
            <Link
              to="/hero-class"
              data-testid="open-hero-class-btn"
              className="inline-block mt-4 px-5 py-2.5 rounded bg-amber-500 hover:bg-amber-400 text-black font-black text-sm tracking-wide"
            >
              🦸 Pick your Class →
            </Link>
          )}
          <div className="text-[10px] text-neutral-500 italic mt-3">
            🚧 Phase 2: A Roc quest will gate the unlock. V1 is open access — try the classes.
          </div>
        </div>
      </div>
    </div>
  );
}
